import { type FC, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material'

export interface DownloadSegment {
  startTime: number
  endTime: number
  transcription: string
}

interface TranscriptDownloadModalProps {
  open: boolean
  onClose: () => void
  title: string
  segments: DownloadSegment[]
  isLoading: boolean
}

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function printTranscripts(title: string, segments: DownloadSegment[], hideSegmentLines: boolean): void {
  const w = window.open('', '_blank')
  if (!w) return
  const safeTitle = escapeHtml(title)
  const rows = segments
    .map(
      (s, i) =>
        (hideSegmentLines
          ? ''
          : `<p class="meta">Segment ${i + 1}: ${formatTimestamp(s.startTime)}–${formatTimestamp(s.endTime)}</p>`) +
        `<p class="text">${s.transcription.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`,
    )
    .join('')
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
<style>
  body { font-family: sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
  .meta { font-size: 0.75rem; color: #666; margin: 1.5rem 0 0.25rem; }
  .text { font-size: 1.25rem; line-height: 1.8; margin: 0; }
  @media print { body { padding: 0; } }
</style>
</head><body><h2>${safeTitle}</h2>${rows}</body></html>`)
  w.document.close()
  w.focus()
  w.print()
  w.close()
}

/**
 * TranscriptDownloadModal — practice-reading modal for podcast and YouTube players.
 *
 * Displays segments in ascending time order with:
 *  - Small caption: "Segment N: X.Xs–Y.Ys"
 *  - Large body text (1.25rem) for comfortable foreign-language reading
 *
 * The Print button writes clean HTML to a new window and calls window.print(),
 * letting the user print or save as PDF via the browser's native dialog.
 *
 * The "Hide segment lines" toggle hides the segment number/timestamp captions
 * both in the modal preview and in the printed output.
 */
export const TranscriptDownloadModal: FC<TranscriptDownloadModalProps> = ({
  open,
  onClose,
  title,
  segments,
  isLoading,
}) => {
  const [hideSegmentLines, setHideSegmentLines] = useState(false)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            {title}
          </Typography>
          <IconButton size="small" aria-label="close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Stack spacing={2}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i}>
                <Skeleton variant="text" width="30%" sx={{ fontSize: '0.75rem' }} />
                <Skeleton variant="text" width="90%" sx={{ fontSize: '1.25rem' }} />
                <Skeleton variant="text" width="75%" sx={{ fontSize: '1.25rem' }} />
              </Box>
            ))}
          </Stack>
        ) : segments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No transcripts found.
          </Typography>
        ) : (
          <Box>
            {segments.map((seg, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                {!hideSegmentLines && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Segment {index + 1}: {formatTimestamp(seg.startTime)}–{formatTimestamp(seg.endTime)}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '1.25rem', lineHeight: 1.8 }}>
                  {seg.transcription}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={() => setHideSegmentLines((prev) => !prev)}
          disabled={isLoading || segments.length === 0}
        >
          {hideSegmentLines ? 'Show segment lines' : 'Hide segment lines'}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => printTranscripts(title, segments, hideSegmentLines)}
          disabled={isLoading || segments.length === 0}
        >
          Print / Save PDF
        </Button>
      </DialogActions>
    </Dialog>
  )
}
